import { experimental_createPersister } from '@tanstack/react-query-persist-client'
import { clear, createStore, del, get, set } from 'idb-keyval'

import { queryClient } from '@shared/providers/src/QueryClientProvider'

const IDBStorage = (idbStore) => ({
  getItem: async (key) => await get(key, idbStore),
  setItem: async (key, value) => await set(key, value, idbStore),
  removeItem: async (key) => await del(key, idbStore),
})

const prefix = 'query'
const isIDBAvailable = () => typeof window !== 'undefined' && 'indexedDB' in window
const isPersistingEnabled = () => {
  const cachePersisted = JSON.parse(localStorage.getItem('cache-persisted'))
  const cachePersistedOverride = JSON.parse(localStorage.getItem('cache-persisted-override'))

  return (
    isIDBAvailable() && cachePersistedOverride !== 'not-persisted' && (cachePersistedOverride === 'persisted' || cachePersisted === true)
  )
}

const getStore = () => {
  if (!isIDBAvailable()) return null
  return createStore('careplus', 'react-query-cache')
}

export const clearIDBStore = () => {
  const store = getStore()
  if (!store) return Promise.resolve()
  return clear(store)
}

/**
 * Custom helper to update both in-memory cache and persisted cache
 * workaround for https://github.com/TanStack/query/issues/6310
 */
export const setQueryData = async (queryKey, data) => {
  // 1. Update in-memory cache
  const newData = typeof data === 'function' ? data(queryClient.getQueryData(queryKey)) : data
  queryClient.setQueryData(queryKey, newData)

  // 2. Check if persistence is enabled
  if (!isPersistingEnabled()) return

  // 3. Get the store and prepare for persistence
  const store = getStore()
  if (!store) return

  // Prepare the storage key (same format as the persister uses)
  const stringifiedQueryKey = JSON.stringify(queryKey)
  const storageKey = `${prefix}-${stringifiedQueryKey}`

  try {
    // 4. Get the current cache entry if it exists
    const existingEntry = await get(storageKey, store)

    // 5. Create a persisted cache entry in the same format as the persister
    const timestamp = Date.now()

    const persistedCache = {
      buster: '', // Cache buster timestamp
      queryKey,
      // Preserve the query hash if it exists in the current entry
      queryHash: existingEntry?.queryHash || stringifiedQueryKey,
      state: {
        data: newData,
        dataUpdateCount: (existingEntry?.state?.dataUpdateCount || 0) + 1,
        dataUpdatedAt: timestamp,
        error: null,
        errorUpdateCount: 0,
        errorUpdatedAt: 0,
        fetchFailureCount: 0,
        fetchFailureReason: null,
        fetchMeta: null,
        fetchStatus: 'idle',
        isInvalidated: false,
        status: 'success',
      },
    }

    // 6. Save to IndexedDB
    await set(storageKey, persistedCache, store)
  } catch (error) {
    console.warn('Failed to persist query data:', error)
  }
}

export const getPersister = () => {
  if (!isPersistingEnabled()) return

  return experimental_createPersister({
    storage: IDBStorage(getStore()),
    maxAge: 1000 * 60 * 60 * 8, // 8 hours,
    serialize: (persistedQuery) => persistedQuery,
    deserialize: (cached) => cached,
    prefix,
  })
}
